import api from '@/api'
import socket from '@/plugins/socket'
import Vue from 'vue'
import i18n from '@/plugins/i18n.plugin'
import {
  defineSyncSchema,
  schemaKinds,
  createSyncState,
  createSyncRequest,
  isStaleResponse,
  collectProtected,
  recordWrite,
  mergeRows,
  syncMutations
} from '@/helpers/storeSync'

const USUARIO_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_USUARIO', kind: 'usuario', insertAt: 'end' })

const state = {
  currentUsuario: null,
  usuarios: [],
  usersLogged: [],
  sync: createSyncState(schemaKinds(USUARIO_SYNC_SCHEMA))
}

const getters = {
  currentUsuario: (state) => state.currentUsuario,

  getUsuarios: (state) => {
    return state.usuarios
  },

  getUserById: (state) => (userId) => {
    return state.usuarios.find(x => x.ID_USUARIO === userId)
  },

  getEmailByUserId: (state) => (userId) => {
    const user = state.usuarios.find(x => x.ID_USUARIO === userId)
    return user ? user.EMAIL : i18n.t('useroles.systemUser')
  },

  usersLogged: (state) => {
    return state.usersLogged.filter(x => x.USUARIO !== state.currentUsuario.USUARIO)
  },
}

const mutations = {
  ...syncMutations,

  SET_CURRENT_USUARIO(state, currentUsuario) {
    state.currentUsuario = currentUsuario
  },

  SET_USUARIOS(state, usuarios) {
    state.usuarios = usuarios.sort((a, b) => a.NOMBRE.localeCompare(b.NOMBRE))
  },

  MERGE_USUARIOS(state, { rows, request }) {
    if (isStaleResponse(state.sync, request)) return
    const protectedByKind = collectProtected(state.sync.stamps, request)
    state.usuarios = mergeRows(state.usuarios, rows, USUARIO_SYNC_SCHEMA, protectedByKind).sort((a, b) => a.NOMBRE.localeCompare(b.NOMBRE))
    state.sync.appliedSeq = request.seq
  },

  SOCKET_addUsuario(state, data) {
    state.usuarios.push(data)
    recordWrite(state.sync, 'usuario', data.ID_USUARIO)
    this.notify(i18n.t('alert.userAdded'), i18n.t('alert.userAdd'), 'success')
  },

  SOCKET_updateEstadoUsuario(state, data) {
    const usuario = state.usuarios.find(x => x.ID_USUARIO === data.userId)
    if (usuario) {
      usuario.ESTADO = data.estado
      recordWrite(state.sync, 'usuario', data.userId)
      this.notify(i18n.t('alert.stateUpdate'), i18n.t('alert.stateUpdateUser', { accion: usuario.ESTADO ? i18n.t('common.activated') : i18n.t('common.activated'), apellidos: usuario.APELLIDOS, nombre: usuario.NOMBRE }), 'success')
    }
  },

  SOCKET_updateUsuario(state, data) {
    const userIndex = state.usuarios.findIndex(x => x.ID_USUARIO === data.ID_USUARIO)
    if (userIndex !== -1) {
      state.usuarios[userIndex] = data
      Vue.set(state.usuarios, userIndex, data)
      recordWrite(state.sync, 'usuario', data.ID_USUARIO)
      this.notify(i18n.t('alert.dataUpdated'), i18n.t('alert.userDataUpdated'), 'success')
    }
  },

  SOCKET_usersLoggedIn(state, data) {
    const currentUsuario = state.currentUsuario

    if (!currentUsuario.ID_SOCKET) {
      for (let i = 0; i < data.length; i++) {
        if (currentUsuario.EMAIL === data[i].USUARIO) {
          currentUsuario.ID_SOCKET = data[i].ID_SOCKET
          state.currentUsuario = currentUsuario
        }
      }
    }

    state.usersLogged = data
  }
}

const actions = {

  deleteCurrentUser({ commit, dispatch }) {
    commit('SET_CURRENT_USUARIO', null)
    commit('SET_USUARIOS', [])
  },

  async fetchCurrentUser({ commit, dispatch }) {
    try {
      const response = await api.usuario.getCurrentUser()
      const currentUsuario = response.data
      commit('SET_CURRENT_USUARIO', currentUsuario)
      const permisos = currentUsuario.ROL.PERMISOS.map(
        p => p.ID_PERMISO
      )

      dispatch('auth/setPermisos', permisos, { root: true })
    } catch (err) {
      console.error(i18n.t('alert.error.startUser'), err)
      throw err
    }
  },

  async fetchUsuarios({ commit, state }) {
    try {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)
      const response = await api.usuario.getUsuarios()
      const usuarios = response.data
      commit('MERGE_USUARIOS', { rows: usuarios, request })
    } catch (err) {
      console.error(i18n.t('alert.error.startUser'), err)
      throw err
    }
  },

  async addUsuario({ commit }, usuario) {
    try {
      await api.usuario.addUsuario(usuario)
    } catch (err) {
      console.error(i18n.t('alert.error.userAddition'), err)
      throw err
    }
  },

  async updateUsuario({ commit }, userData) {
    try {
      await api.usuario.updateUsuario(userData)
    } catch (err) {
      console.error(i18n.t('alert.error.userUpdate'), err)
      throw err
    }
  },

  async updateEstadoUsuario({ commit }, userData) {
    try {
      await api.usuario.updateEstadoUsuario(userData)
    } catch (err) {
      console.error(i18n.t('alert.error.startUser'), err)
      throw err
    }
  },

  async updatePasswordUsuario({ commit }, userData) {
    try {
      const response = await api.usuario.updatePasswordUsuario(userData)
      return response.data.msg
    } catch (err) {
      console.error("Error al actualizar usuario:", err)
      throw err
    }
  },

  userLoggedIn({ state }) {
    socket.emit('loggedIn', state.currentUsuario)
  },
}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
